import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Download } from 'lucide-react-native'
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { format } from 'date-fns'
import { Button, InlineAlert } from '@chillout/ui'
import { AdminScreen } from '../AdminScreen'
import { AdminTable } from '../shared/Table'
import { AdminFilterDropdown, type FilterGroup } from '../shared/AdminFilterDropdown'
import { Card } from '../../shared/Card'
import { DateRangePicker } from './DateRangePicker'
import { AuditLogDetailModal } from './AuditLogDetailModal'
import { useAuditLog } from './useAuditLog'
import { useAdminList } from './useAdminList'
import { exportAuditLogCsv, type AuditExportFilterLabels } from './exportAuditLogCsv'
import { PAGE_SIZE, sortChangesEntries, type AuditLogEntry } from './auditLogConstants'

function formatDateTime(createdAt: string, t: (key: string) => string, language: string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()
  const dayLabel = isToday
    ? t('common.today')
    : isYesterday
      ? t('common.yesterday')
      : date.toLocaleDateString(language, { day: 'numeric', month: 'short' })
  const timeLabel = date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })
  return `${dayLabel}, ${timeLabel}`
}

function formatRelativeTime(createdAt: string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 4) return `${diffWeeks}w ago`
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

function toTitleCase(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function hasUuidValue(val: unknown): boolean {
  if (typeof val === 'string') return UUID_RE.test(val)
  if (val && typeof val === 'object' && 'before' in val) {
    const v = val as { before: unknown; after: unknown }
    return (typeof v.before === 'string' && UUID_RE.test(v.before)) ||
      (typeof v.after === 'string' && UUID_RE.test(v.after))
  }
  return false
}

function displayValue(val: unknown): string {
  if (val && typeof val === 'object' && 'name' in val) return String((val as { name: string }).name)
  if (typeof val === 'string') return toTitleCase(val)
  return String(val ?? 'N/A')
}

function DetailsCell({ entry, size = 'sm' }: { entry: AuditLogEntry; size?: 'sm' | 'xs' }) {
  if (!entry.changes) return <Text className={`font-normal text-primary ${size === 'xs' ? 'text-xs' : 'text-sm'}`}>—</Text>

  const textClass = size === 'xs' ? 'text-xs' : 'text-sm'
  const filtered = sortChangesEntries(Object.entries(entry.changes).filter(([, val]) => !hasUuidValue(val)))

  if (filtered.length === 0) return <Text className={`font-normal text-primary ${textClass}`}>—</Text>

  return (
    <View className="gap-0.5">
      {filtered.map(([key, val]) => {
        const label = toTitleCase(key)
        if (val === true) {
          return (
            <Text key={key} className={`${textClass} font-semibold text-primary`}>{label}</Text>
          )
        }
        if (val && typeof val === 'object' && 'before' in val && 'after' in val) {
          return (
            <View key={key} className="flex-row flex-wrap">
              <Text className={`${textClass} font-semibold text-primary`}>{label}:&nbsp;</Text>
              <Text className={`${textClass} font-normal text-primary`}>
                {displayValue((val as { before: unknown }).before)} → {displayValue((val as { after: unknown }).after)}
              </Text>
            </View>
          )
        }
        return (
          <View key={key} className="flex-row flex-wrap">
            <Text className={`${textClass} font-semibold text-primary`}>{label}:&nbsp;</Text>
            <Text className={`${textClass} font-normal text-primary`}>{String(val)}</Text>
          </View>
        )
      })}
    </View>
  )
}

export function AuditLogScreen() {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [activeFilters, setActiveFilters] = useState<Record<string, string | string[] | undefined>>({})
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 6)
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date()
    d.setHours(23, 59, 59, 999)
    return d
  })

  const { data: adminList = [] } = useAdminList()

  const auditLogQuery = useAuditLog({
    page,
    dateFrom,
    dateTo,
    eventGroups: activeFilters.eventGroup as string[] | undefined,
    actorAdminIds: activeFilters.actorAdminId as string[] | undefined,
  })

  const entries = useMemo(() => auditLogQuery.data?.entries ?? [], [auditLogQuery.data])
  const totalCount = auditLogQuery.data?.totalCount ?? 0
  const pageCount = Math.ceil(totalCount / PAGE_SIZE)

  const hasMultipleAdmins = adminList.length > 1

  const filterGroups = useMemo<FilterGroup[]>(
    () => [
      {
        id: 'eventGroup',
        label: t('auditLog.filters.action'),
        multiSelect: true,
        options: [
          { label: t('auditLog.eventGroups.cashierMgmt'), value: 'cashier_mgmt' },
          { label: t('auditLog.eventGroups.deviceMgmt'), value: 'device_mgmt' },
          { label: t('auditLog.eventGroups.settings'), value: 'settings' },
          { label: t('auditLog.eventGroups.loyalty'), value: 'loyalty' },
          { label: t('auditLog.eventGroups.gdpr'), value: 'gdpr' },
          { label: t('auditLog.eventGroups.adminAuth'), value: 'admin_auth' },
        ],
      },
      ...(hasMultipleAdmins
        ? [{
            id: 'actorAdminId',
            label: t('auditLog.columns.actionBy'),
            multiSelect: true,
            options: adminList.map((admin) => ({ label: admin.name, value: admin.id })),
          }]
        : []),
    ],
    [t, adminList, hasMultipleAdmins],
  )

  const columns = useMemo<ColumnDef<AuditLogEntry>[]>(
    () => [
      {
        id: 'dateTime',
        header: t('auditLog.columns.dateTime'),
        size: 90,
        cell: ({ row }) => {
          const date = new Date(row.original.createdAt)
          const now = new Date()
          const isToday = date.toDateString() === now.toDateString()
          const yesterday = new Date(now)
          yesterday.setDate(yesterday.getDate() - 1)
          const isYesterday = date.toDateString() === yesterday.toDateString()
          const dayLabel = isToday
            ? t('common.today')
            : isYesterday
              ? t('common.yesterday')
              : date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })
          const timeLabel = date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }).replace(/ /g, ' ')
          return (
            <Text className="text-sm font-normal text-primary">
              {dayLabel}, <Text className="text-secondary">{timeLabel}</Text>
            </Text>
          )
        },
      },
      {
        id: 'action',
        header: t('auditLog.columns.action'),
        size: 120,
        cell: ({ row }) => (
          <Text className="text-sm font-normal text-primary">
            {t(`auditLog.eventTypes.${row.original.eventType}` as never)}
          </Text>
        ),
      },
      {
        id: 'target',
        header: t('auditLog.columns.target'),
        size: 120,
        cell: ({ row }) => (
          <View>
            <Text className="text-sm font-normal text-primary" numberOfLines={1}>
              {row.original.targetEntityName ?? '—'}
            </Text>
            {row.original.targetEntityId ? (
              <Text className="text-xs font-normal text-muted" numberOfLines={1}>
                ...{row.original.targetEntityId.slice(-5)}
              </Text>
            ) : null}
          </View>
        ),
      },
      {
        id: 'details',
        header: t('auditLog.columns.details'),
        size: 200,
        cell: ({ row }) => <DetailsCell entry={row.original} />,
      },
      ...(hasMultipleAdmins
        ? [{
            id: 'actionBy',
            header: t('auditLog.columns.actionBy'),
            size: 120,
            cell: ({ row }: { row: { original: AuditLogEntry } }) => (
              <View>
                <Text className="text-sm font-normal text-primary" numberOfLines={1}>
                  {row.original.actorName ?? '—'}
                </Text>
                {row.original.actorEmail ? (
                  <Text className="text-xs font-normal text-secondary" numberOfLines={1}>
                    {row.original.actorEmail}
                  </Text>
                ) : null}
              </View>
            ),
          }]
        : []),
    ],
    [t, i18n.language, hasMultipleAdmins],
  )

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const hasActiveFilters = Object.values(activeFilters).some(Boolean)
  const isFetching = auditLogQuery.isFetching && !auditLogQuery.isLoading

  function handleFilterChange(id: string, value: string | string[] | undefined) {
    setActiveFilters((prev) => ({ ...prev, [id]: value }))
    setPage(1)
  }

  async function handleExport() {
    setIsExporting(true)
    setExportError(false)
    try {
      const eventGroups = (activeFilters.eventGroup as string[] | undefined) ?? []
      const actorAdminIds = (activeFilters.actorAdminId as string[] | undefined) ?? []
      const dateRange = `${format(dateFrom, 'd MMM yyyy')} – ${format(dateTo, 'd MMM yyyy')}`

      const filterLabels: AuditExportFilterLabels = {
        dateRange,
        eventGroup: eventGroups.length > 0
          ? eventGroups.map((g) => t(`auditLog.eventGroups.${g}` as never)).join(', ')
          : undefined,
        actor: actorAdminIds.length > 0
          ? actorAdminIds.map((id) => adminList.find((a) => a.id === id)?.name ?? id).join(', ')
          : undefined,
      }

      await exportAuditLogCsv({
        dateFrom,
        dateTo,
        eventGroups,
        actorAdminIds,
      }, filterLabels, t)
    } catch {
      // Already reported to Sentry inside exportAuditLogCsv — this catch
      // is just for the user-facing error state.
      setExportError(true)
    } finally {
      setIsExporting(false)
    }
  }

  function handleDateChange(from: Date, to: Date) {
    setDateFrom(from)
    setDateTo(to)
    setPage(1)
  }

  return (
    <AdminScreen
      title={t('adminDashboard.auditLogTitle')}
      subtitle={t('adminDashboard.auditLogSubtitle')}
      actions={
        <Button
          label={t('auditLog.export')}
          loadingLabel={t('auditLog.exporting')}
          LeftIcon={Download}
          fullWidth={false}
          loading={isExporting}
          accessibilityLabel={t('auditLog.exportAccessibilityLabel')}
          onPress={handleExport}
        />
      }
    >
      <Card variant="dashboard" className="p-6">
        {exportError ? (
          <View className="mb-4">
            <InlineAlert message={t('auditLog.exportError')} />
          </View>
        ) : null}
        <View className="items-end gap-2 lg:flex-row lg:items-center lg:justify-end">
          <AdminFilterDropdown
            groups={filterGroups}
            activeFilters={activeFilters}
            onChange={handleFilterChange}
            filterLabel={t('auditLog.filters.label')}
            clearLabel={t('auditLog.filters.clear')}
          />
          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={handleDateChange}
          />
        </View>

        <AdminTable
          table={table}
          minWidth={530}
          alignRows="top"
          isLoading={auditLogQuery.isLoading}
          isError={auditLogQuery.isError}
          isFetching={isFetching}
          hasActiveFilters={hasActiveFilters}
          onRowPress={setSelectedEntry}
          rowAccessibilityLabel={(entry) =>
            t('auditLog.rowAccessibilityLabel', {
              action: t(`auditLog.eventTypes.${entry.eventType}` as never),
              admin: entry.actorName ?? '—',
              time: formatDateTime(entry.createdAt, t, i18n.language),
            })
          }
          renderMobileRow={(entry) => (
            <>
              <View className="flex-row items-center justify-between gap-3">
                <Text className="min-w-0 flex-1 text-sm font-semibold text-primary" numberOfLines={1}>
                  {t(`auditLog.eventTypes.${entry.eventType}` as never)}
                </Text>
                <Text className="shrink-0 text-xs font-normal text-muted" numberOfLines={1}>
                  {hasMultipleAdmins ? `${entry.actorName ?? '—'} · ` : ''}{formatRelativeTime(entry.createdAt)}
                </Text>
              </View>
              <View className="mt-1">
                <DetailsCell entry={entry} size="xs" />
              </View>
            </>
          )}
          onRetry={() => void auditLogQuery.refetch()}
          errorMessage={t('auditLog.loadError')}
          noResultsDescription={t('auditLog.noResults')}
          emptyIcon={ClipboardList}
          emptyTitle={t('auditLog.emptyTitle')}
          emptyDescription={t('auditLog.emptyDescription')}
          page={page}
          pageCount={pageCount}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPreviousPage={() => setPage((p) => Math.max(p - 1, 1))}
          onNextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        />
      </Card>
      <AuditLogDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </AdminScreen>
  )
}